/**
 * View component for /admin/authoring/courses/:courseId/update
 *
 * Updates a single course from a copy of the selcted course
 * as defined in the course reducer
 */

// import primary libraries
import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { history, withRouter } from 'react-router-dom';

// import third-party libraries
import _ from 'lodash';

// import actions
import * as courseActions from '../courseActions';
import * as sponsorActions from '../../sponsor/sponsorActions';
import * as studyActions from '../../study/studyActions';
import * as contentModuleActions from '../../contentModule/contentModuleActions';

// import global components
import TrainingManagementLayout from '../../admin/components/TrainingManagementLayout.js.jsx';
import Base from "../../../global/components/BaseComponent.js.jsx";
import Breadcrumbs from '../../../global/components/routing/Breadcrumbs.js.jsx';

// import module components
import CourseForm from '../components/CourseForm.js.jsx';

// import course css modules
import courseStyles from '../courseModuleStyles.css';

class AdminUpdateCourse extends Base {
  constructor(props) {
    super(props);
    const { selectedCourse, courseMap } = this.props;
    const course = courseMap[selectedCourse.id] ? _.cloneDeep(courseMap[selectedCourse.id]) : {}
    this.state = {
      course
      // NOTE: we don't want to change the store, just make changes to a copy
      , formHelpers: {
        isNewOverviewContent: true
        , statuses: ['published', 'draft', 'archived']
        , ordered: course.ordered ? 'yes' : 'no'
      }
    }
    this._bind(
      '_handleFormChange'
      , '_handleFormSubmit'
    );
  }

  componentDidMount() {
    const { dispatch, match } = this.props;
    dispatch(courseActions.fetchSingleIfNeeded(match.params.courseId)).then((action) => {
      dispatch(studyActions.fetchListIfNeeded("_organization", action.item._organization));
      dispatch(contentModuleActions.fetchListIfNeeded("_organization", action.item._organization, "_group", null, "published", true));
      dispatch(contentModuleActions.fetchListIfNeeded("_group", action.item._group, "published", true));
    });
    dispatch(sponsorActions.fetchListIfNeeded());
    dispatch(contentModuleActions.fetchListIfNeeded("_organization", null, "_group", null, "published", true));
  }

  componentWillReceiveProps(nextProps) {
    /** this updates the state if ANY props have changed, resetting the "course" object from the store
     * this means that it will RESET the form on anything that results in a props change
     * like the _group field, making it impossible to change that field
     * instead, only set the state if the value in the reducer has changed
    **/

    const prevSelectedCourse = this.props.selectedCourse
    const prevCourseMap = this.props.courseMap;
    const { selectedCourse, courseMap } = nextProps;
    const prevCourse = prevCourseMap[prevSelectedCourse.id] ? { ...prevCourseMap[prevSelectedCourse.id] } : {}
    const course = courseMap[selectedCourse.id] ? { ...courseMap[selectedCourse.id] } : {}

    if(! _.isEqual(prevCourse, course)) {
      const course = courseMap[selectedCourse.id] ? _.cloneDeep(courseMap[selectedCourse.id]) : {}
      this.setState({
        course
        //we don't want to actually change the store's course, just use a copy
        , formHelpers: {
          isNewOverviewContent: true
          , statuses: ['published', 'draft', 'archived']
          , ordered: course.ordered ? 'yes' : 'no'
        }
      })
    }
  }

  _handleFormChange(e) {
    const { dispatch } = this.props;
    const { course } = this.state;

    let newState = _.update( this.state, e.target.name, function() {
      return e.target.value;
    });
    // this.setState(newState); // can't update and then use state again
    // funcationality when selecting allSponsors and allStudies
    if(e.target.name === 'course._organization') {
      dispatch(studyActions.fetchListIfNeeded("_organization", e.target.value));
      dispatch(contentModuleActions.fetchListIfNeeded("_organization", e.target.value, "_group", null, "published", true));
      // clear study so the form doesn't show non needed values
      newState.course._group = null;
      newState.course._contentModules = [];
      // this.setState({
      //   course
      // });
    }
    if(e.target.name === 'course._group') {
      dispatch(contentModuleActions.fetchListIfNeeded("_group", e.target.value, "published", true));
    }
    this.setState(newState);
  }

  _handleFormSubmit(e) {
    const { dispatch, history } = this.props;
    e.preventDefault();

    const { formHelpers } = this.state;
    let newCourse = _.cloneDeep(this.state.course);
    newCourse.ordered = (formHelpers.ordered == 'yes')

    dispatch(courseActions.sendUpdateCourse(newCourse)).then((action) => {
      if(action.success) {
        history.push(`/admin/content-management/studies/${action.item._group}/courses/${action.item._id}`)
      } else {
        // console.log("Response Error:");
        // console.log(action);
        alert(`ERROR: ${action.error}`);
      }
    });
  }

  render() {
    const {
      courseMap
      , location
      , match
      , selectedCourse
      , sponsorStore
      , studyStore
      , contentModuleStore
    } = this.props;
    const { course, formHelpers } = this.state;
    const isEmpty = (!course || course._id === null || course._id === undefined);
    const allSponsors = sponsorStore.util.getList("all") || [];
    const allStudies = studyStore.util.getList("_organization", course._organization) || [];
    let contentModules = [];
    // get all needed content modules
    const universalContentModules = contentModuleStore.util.getList("_organization", null, "_group", null, "published", true) || [];
    const sponsorContentModules = contentModuleStore.util.getList("_organization", course._organization, "_group", null, "published", true) || [];
    const studyContentModules = contentModuleStore.util.getList("_group", course._group, "published", true) || [];
    // combine all arrays & drop duplicates
    contentModules = _.unionBy(universalContentModules, sponsorContentModules, studyContentModules, "_id");
    return  (
      <TrainingManagementLayout>
        <div className="flex">
          <section className="section">
            <div className="yt-container">
              <Breadcrumbs links={location.state.breadcrumbs} />
            </div>
            { isEmpty ?
              (selectedCourse.isFetching ? <h2>Loading...</h2> : <h2>Empty.</h2>)
              :
              <CourseForm
                cancelLink={`/admin/content-management/courses/${course._id}`}
                course={course}
                formHelpers={formHelpers}
                formTitle={match.params.studyId ? "Update this study's course" : 'Update course'}
                formType="update"
                fromStudy={match.params.studyId ? true : false}
                handleFormChange={this._handleFormChange}
                handleFormSubmit={this._handleFormSubmit}
                allSponsors={allSponsors}
                allStudies={allStudies}
                contentModules={contentModules}
                />
            }
          </section>
        </div>
      </TrainingManagementLayout>
    )
  }
}

AdminUpdateCourse.propTypes = {
  dispatch: PropTypes.func.isRequired
}

const mapStoreToProps = (store) => {
  /**
  * NOTE: Yote refer's to the global Redux 'state' as 'store' to keep it mentally
  * differentiated from the React component's internal state
  */
  return {
    courseMap: store.course.byId
    , selectedCourse: store.course.selected
    , sponsorStore: store.sponsor
    , studyStore: store.study
    , contentModuleStore: store.contentModule
  }
}

export default withRouter(
  connect(
    mapStoreToProps
  )(AdminUpdateCourse)
);



// WEBPACK FOOTER //
// modules/course/views/AdminUpdateCourse.js.jsx